import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useBills } from '../contexts/BillsContext';
import { Bill, BILL_STATUS_CONFIG } from '../../../types/accounting';
import { RecordPaymentModal } from '../../invoices/components/RecordPaymentModal';
import { PaymentHistory } from '../../invoices/components/PaymentHistory';
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import { formatDateAU, isDateOverdue } from '../../../utils/dateUtils';

const formatDateStr = formatDateAU;

interface BillViewModalProps {
  show: boolean;
  bill: Bill | null;
  onHide: () => void;
  onEdit?: (bill: Bill) => void;
  onVoided?: () => void;
}

export function BillViewModal({ show, bill, onHide, onEdit, onVoided }: BillViewModalProps) {
  const { voidBill, billActionLoading, fetchPayments, deletePayment, paymentsLoading, deletePaymentLoading, recordPayment, recordPaymentLoading } = useBills();
  const status = bill?.status ? BILL_STATUS_CONFIG[bill.status] : null;
  const [voidError, setVoidError] = useState<string | null>(null);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  // Bumped after a payment so the embedded PaymentHistory re-fetches (audit: low).
  const [paymentReloadKey, setPaymentReloadKey] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);

  const formatCurrency = (amount: number, currency = 'AUD') =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount);

  const isOverdue = (b: Bill) => {
    if (b.status !== 'APPROVED' && b.status !== 'SENT') return false;
    if (!(b.amountDue && b.amountDue > 0)) return false;
    return isDateOverdue(b.dueDate);
  };

  const handleVoid = async () => {
    if (!bill) return;
    setVoidError(null);
    try {
      await voidBill(bill.id);
      onVoided?.();
      onHide();
    } catch (err: any) {
      setVoidError(err?.message || 'Failed to void bill');
    }
  };

  if (!bill) return null;

  const overdue = isOverdue(bill);
  const canEdit = bill.status !== 'PAID' && bill.status !== 'VOIDED';
  const canVoid = bill.status === 'APPROVED' || bill.status === 'SENT';
  const canRecordPayment = (bill.status === 'APPROVED' || bill.status === 'SENT' || bill.status === 'PAID') && (bill.amountDue ?? 0) > 0;
  const isLoading = billActionLoading === bill.id;

  const subtotal = bill.lineItems?.reduce((s, li) => s + (li.lineAmount ?? 0), 0) ?? 0;
  const totalTax = bill.lineItems?.reduce((s, li) => s + (li.taxAmount ?? 0), 0) ?? 0;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="md-down">
      <Modal.Header closeButton className="border-0 pb-0">
        <div className="d-flex flex-column">
          <Modal.Title className="d-flex align-items-center gap-2">
            <span>{bill.billNumber}</span>
            {status && (
              <span className={`badge ${status.badgeClass}`} style={{ fontSize: '0.7rem' }}>
                <i className={`bi ${status.icon} me-1`} />
                {status.label}
              </span>
            )}
            {overdue && (
              <span className="badge badge-light-danger" style={{ fontSize: '0.7rem' }}>
                <i className="bi bi-exclamation-circle-fill me-1" />
                Overdue
              </span>
            )}
          </Modal.Title>
          <span className="text-muted fs-7 mt-1">{bill.contactName || '—'}</span>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-4">
        {voidError && (
          <div className="alert alert-danger py-2 mb-4 d-flex align-items-center" style={{ fontSize: '0.8125rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />
            {voidError}
          </div>
        )}

        <div className="rounded-3 p-4 mb-4" style={{ background: 'var(--bs-tertiary-bg)' }}>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <span className="text-muted fs-7 d-block mb-1">Total</span>
              <span className="fw-bold fs-3">{formatCurrency(bill.total ?? 0, bill.currencyCode)}</span>
            </div>
            {(bill.amountDue ?? 0) > 0 && (
              <div className="text-end">
                <span className="text-muted fs-7 d-block mb-1">Amount Due</span>
                <span className={`fw-bold fs-3 ${overdue ? 'text-danger' : 'text-warning'}`}>
                  {formatCurrency(bill.amountDue ?? 0, bill.currencyCode)}
                </span>
              </div>
            )}
            {(bill.amountDue ?? 0) === 0 && bill.status === 'PAID' && (
              <div className="text-end">
                <span className="text-muted fs-7 d-block mb-1">Amount Due</span>
                <span className="fw-bold fs-3 text-success">{formatCurrency(0, bill.currencyCode)}</span>
              </div>
            )}
          </div>
          <div className="d-flex gap-4 mt-3 pt-3 border-top" style={{ borderColor: 'var(--bs-border-color) !important' }}>
            <div>
              <span className="text-muted fs-8 d-block">Subtotal</span>
              <span className="fw-semibold fs-7">{formatCurrency(subtotal, bill.currencyCode)}</span>
            </div>
            <div>
              <span className="text-muted fs-8 d-block">Tax</span>
              <span className="fw-semibold fs-7">{formatCurrency(totalTax, bill.currencyCode)}</span>
            </div>
            {(bill.amountPaid ?? 0) > 0 && (
              <div>
                <span className="text-muted fs-8 d-block">Paid</span>
                <span className="fw-semibold fs-7 text-success">{formatCurrency(bill.amountPaid!, bill.currencyCode)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="d-flex flex-column gap-3">
          <div>
            <span className="text-muted fs-8 d-block mb-1">Supplier</span>
            <span className="fw-semibold fs-7">{bill.contactName || '—'}</span>
          </div>
          <div className="row g-3">
            <div className="col-sm-6">
              <span className="text-muted fs-8 d-block mb-1">Bill Date</span>
              <span className="fw-semibold fs-7">{formatDateStr(bill.date)}</span>
            </div>
            <div className="col-sm-6">
              <span className="text-muted fs-8 d-block mb-1">Due Date</span>
              <span className={`fw-semibold fs-7 ${overdue ? 'text-danger' : ''}`}>
                {formatDateStr(bill.dueDate)}
                {overdue && <i className="bi bi-exclamation-circle-fill text-danger ms-2" />}
              </span>
            </div>
          </div>
          {bill.reference && (
            <div>
              <span className="text-muted fs-8 d-block mb-1">Reference</span>
              <span className="fw-semibold fs-7">{bill.reference}</span>
            </div>
          )}
        </div>

        {bill.lineItems && bill.lineItems.length > 0 && (
          <div className="mt-4 pt-3 border-top">
            <span className="text-muted fs-8 d-block mb-2">Line Items</span>
            <div className="table-responsive">
              <table className="table table-sm table-row-bordered mb-0">
                <thead>
                  <tr className="fs-8 text-muted">
                    <th>Description</th>
                    <th className="text-end" style={{ width: '60px' }}>Qty</th>
                    <th className="text-end" style={{ width: '100px' }}>Unit Price</th>
                    <th className="text-end" style={{ width: '100px' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {bill.lineItems.map((item, idx) => (
                    <tr key={idx} className="fs-7">
                      <td>{item.description || '-'}</td>
                      <td className="text-end">{item.quantity ?? 1}</td>
                      <td className="text-end">{formatCurrency(item.unitAmount ?? 0, bill.currencyCode)}</td>
                      <td className="text-end fw-semibold">{formatCurrency(item.lineAmount ?? 0, bill.currencyCode)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <PaymentHistory
          documentId={bill.id}
          currencyCode={bill.currencyCode}
          fetchPayments={fetchPayments}
          deletePayment={deletePayment}
          paymentsLoading={paymentsLoading}
          deletePaymentLoading={deletePaymentLoading}
          reloadKey={paymentReloadKey}
        />

        <div className="mt-4 pt-3 border-top">
          <div className="d-flex flex-wrap gap-2">
            {canRecordPayment && (
              <button className="btn btn-success btn-sm d-flex align-items-center gap-2" onClick={() => setShowRecordPayment(true)}>
                <i className="bi bi-cash-coin" />
                Record Payment
              </button>
            )}
            {canEdit && onEdit && (
              <button
                className="btn btn-light-primary btn-sm d-flex align-items-center gap-2"
                onClick={() => { onHide(); onEdit(bill); }}
              >
                <i className="bi bi-pencil" />
                Edit Bill
              </button>
            )}
            {canVoid && (
              <button
                className="btn btn-light-danger btn-sm d-flex align-items-center gap-2 ms-auto"
                onClick={handleVoid}
                disabled={isLoading}
              >
                {isLoading ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-x-circle" />}
                Void Bill
              </button>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 d-flex justify-content-between align-items-center">
        <button className="btn btn-light" onClick={onHide}>Close</button>
        <button
          type="button"
          className="btn btn-sm btn-primary d-flex align-items-center gap-2"
          disabled={pdfLoading}
          onClick={async () => {
            setPdfLoading(true);
            try {
              const res = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/generateBillPdfHttp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ billId: bill.id }),
              });
              if (!res.ok) throw new Error('Failed to generate PDF');
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `bill-${bill.billNumber}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
            } catch {
              // ignore
            } finally {
              setPdfLoading(false);
            }
          }}
        >
          {pdfLoading ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-download" />}
          Download PDF
        </button>
      </Modal.Footer>

      <RecordPaymentModal
        show={showRecordPayment}
        documentId={bill.id}
        documentNumber={bill.billNumber}
        amountDue={bill.amountDue ?? 0}
        currencyCode={bill.currencyCode}
        onSuccess={() => setPaymentReloadKey((k) => k + 1)}
        onHide={() => setShowRecordPayment(false)}
        recordPayment={(p) => recordPayment({ billId: p.documentId, method: p.method, amount: p.amount, date: p.date, reference: p.reference })}
        recordPaymentLoading={recordPaymentLoading}
      />
    </Modal>
  );
}

export default BillViewModal;
